import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { isSupportedLocale, LOCALE_NATIVE_NAMES, SUPPORTED_LOCALES } from '@financialharmony/i18n';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { useUiLocale } from '@/i18n/useUiLocale';

/**
 * Profile → Language: every language listed by its own name, choosing one saves it to the account.
 * The selected row always shows the language the app is in — a failed save leaves it where it was
 * and says so.
 */
export function LanguageSection() {
  const t = useTranslations('settings.language');
  const { locale, setLocale } = useUiLocale();
  const [pending, setPending] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  async function choose(next: string) {
    if (!isSupportedLocale(next) || next === locale) return;
    setSaveFailed(false);
    setPending(next);
    try {
      setSaveFailed(!(await setLocale(next)));
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">{t('title')}</h2>
        <p className="text-sm text-muted-foreground">{t('description')}</p>
      </div>
      <RadioGroup value={locale} onValueChange={choose} disabled={pending !== null} aria-label={t('title')}>
        {SUPPORTED_LOCALES.map((code) => (
          <div
            key={code}
            className={cn(
              'flex items-center gap-3 rounded-md border px-3 py-2.5',
              code === locale ? 'border-primary bg-accent' : 'border-border',
            )}
          >
            <RadioGroupItem id={`language-${code}`} value={code} />
            <Label htmlFor={`language-${code}`} lang={code} className="flex-1 cursor-pointer font-medium">
              {LOCALE_NATIVE_NAMES[code]}
            </Label>
            {pending === code && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        ))}
      </RadioGroup>
      {saveFailed && (
        <Alert variant="destructive">
          <AlertDescription>{t('saveFailed')}</AlertDescription>
        </Alert>
      )}
    </section>
  );
}
